// The Terminal console's live tail (#4396, epic #4391): ONE `AppendLogSpec`
// riding the shared append-log loop (`lib/appendLogStream.ts`) — the same
// posture the sessions and fleet-run views use for their own append logs
// (`lib/sse-one-seam.test.ts` requires each reconnect posture to have exactly
// one live caller declared in that literal shape, which is why this module
// never repeats it, even in prose).
//
// Also builds the console's SYNTHETIC system rows — boot milestones and
// connection-state lines — as ordinary `TerminalEvent`s so they ride the SAME
// renderer, buffer and windowing every backfilled row does (`TerminalRow.tsx`,
// `lib/terminal/buffer.ts`). Each carries a STABLE id, so
// `TerminalRingBuffer.merge`'s id-dedup is what keeps a milestone rendered
// exactly once even though `useTerminalSystemLines` recomputes its memo on
// every reactive tick.
//
// WHAT THE THREE BOOT MILESTONES ARE BOUND TO, AND WHY NOT THE WIRE.
// #4396 names "dial -> 2xx (authenticated as <login>)" and a "stream `ready`
// frame (tailing <org>)". Neither exists on THIS route: `SseEvent.Ready`
// (`packages/domain/src/wire/sse-frames.ts`) is `/v1/streams`-only —
// `/v1/terminal/events/stream` never emits one — and this route's own 2xx says
// only "the connection opened", not "the caller is who they claim". The
// nearest REAL events actually available, bound one-to-one:
//   - `GET /v1/me` resolving is what proves the caller's identity ->
//     "authenticated as <login>".
//   - `GET /v1/terminal/status` resolving is the "status fetched" milestone.
//   - This stream's own `onOpen` (a genuine 2xx-with-body,
//     `lib/appendLogStream.ts`) is what proves the tail itself is open ->
//     "tailing <org>".
// Every line fires off a signal that genuinely became true, once, and never
// re-fires — nothing here is timed.
//
// RECONNECT LINES AND THE POSTURE'S OWN LIMIT. Under `announced_only`, the
// ONLY close that reconnects BY ITSELF is the server's routine ~10-minute
// recycle (`bye`) — a genuine drop (`dropped`/`unavailable`) stops at `error`
// and needs a person to retry, by the posture's own design
// (`lib/sse-subscribe.ts`'s `shouldReconnect`). So the live->connecting->live
// path this module renders as "reconnecting…" / "reconnected" is the recycle, proven safe by `terminal-stream.test.ts` the same way
// `sessionStream.test.ts` proves it for the session view: resumed via
// `Last-Event-ID`, no gap, no duplicate. A genuine drop instead renders
// "connection lost" plus the stream's own `retry` (`Terminal.tsx` wires the
// `<ErrorState/>` for it) — Done-when #3 ("a dropped connection recovers by
// itself") holds for the recycle; it does NOT hold for a real network drop
// under this posture, and that is stated here rather than papered over.
// `retry()` is also a clean slate (no `Last-Event-ID`), so `Terminal.tsx`
// pairs it with a re-run of the backfill's own page-one fetch to close the
// gap a bare retry would otherwise leave silently unfilled.

import {
  SseEvent,
  type TerminalActor,
  type TerminalEvent,
  TerminalEventSchema,
  type TerminalLevel,
} from '@developerz/domain';
import { createEffect, createMemo, createSignal, on, untrack } from 'solid-js';
import { type AppendLogStream, useAppendLogStream } from './appendLogStream';
import type { StreamStatus } from './sseFrames';
import { TERMINAL_BUFFER_CAPACITY } from './terminal/buffer';

const TERMINAL_STREAM_PATH = '/v1/terminal/events/stream';

/**
 * `useAppendLogStream`'s per-resource id accessor. The Terminal has no
 * resource id — it is account-scoped, not session- or run-scoped — so this is
 * a stable constant; an org switch is already `useAppendLogStream`'s own
 * concern (it reads `urlOrg()` inside its effect and tears the connection
 * down + reopens under the new `x-dz-account` header, the same behaviour
 * `sessionStream.test.ts` pins for the session view).
 */
const TERMINAL_STREAM_RESOURCE_ID = 'org';

/**
 * Open the account's live tail. One connection per tab — every pane is a
 * filter over this SAME stream (#4397), never a second dial of it.
 *
 * `verbose` (#4399 slice 08): whether the ONE shared dial should carry
 * `?verbose=true`, widening the tail to include `task_run_events` rows
 * (tool_call, native_harness_turn, run_started/finished/released) alongside
 * the audit ones — the OR of every pane's own toggle
 * (`use-terminal-panes.ts`'s `anyVerbose`), since there is nowhere else for a
 * per-pane knob to land on a single-connection stream. A pane whose own
 * toggle is off still filters the extra rows back out client-side
 * (`lib/terminal/filter.ts`'s `filterVerboseRows`).
 *
 * `redial()`, never `retry()`, on a genuine flip: this is the resume-cursor
 * fix `appendLogStream.ts` shipped for exactly this case — the rows already
 * shown and the `Last-Event-ID` both survive, only the query string on the
 * NEXT dial changes (`dialAppendLog`'s `spec.path(streamId)` read, made
 * inside the dial closure so it always reads the CURRENT value rather than
 * needing Solid's tracking to reach through an async reconnect). No redial on
 * the first run (`prevVerbose === undefined`) — the very first dial already
 * reads the starting value, so redialing there would just be a second,
 * pointless connection.
 */
export function useTerminalStream(
  token: string | null,
  verbose: () => boolean,
): AppendLogStream<TerminalEvent> {
  const stream = useAppendLogStream(() => TERMINAL_STREAM_RESOURCE_ID, token, {
    // UNTRACKED on purpose. `dialAppendLog` calls `spec.path(streamId)`
    // inside the dial closure `sseSubscribe` invokes SYNCHRONOUSLY on the
    // very first attempt — still inside `useAppendLogStream`'s own effect's
    // tracking window. A plain `verbose()` read here would therefore make
    // THAT effect independently subscribe to `verbose` too, so a flip fires
    // TWO redials — one from that hidden subscription, one from the explicit
    // `stream.redial()` below (caught empirically: two `fetch` calls for one
    // `setVerbose`). `untrack` keeps this a plain "read the current value"
    // the way `dial()`'s LATER, async-context calls already are — the
    // explicit `redial()` effect below is the only thing allowed to trigger
    // a re-dial on a verbose change.
    path: () => (untrack(verbose) ? `${TERMINAL_STREAM_PATH}?verbose=true` : TERMINAL_STREAM_PATH),
    event: SseEvent.Terminal,
    schema: TerminalEventSchema,
    // The Terminal console has no end (unlike a session or a fleet run) — see
    // `AppendLogSpec.retain`'s own comment. Reusing `Terminal.tsx`'s own
    // `TerminalRingBuffer` cap rather than inventing a second number: nothing
    // downstream can render a row this hook dropped that the ring buffer
    // would not already have evicted itself.
    retain: TERMINAL_BUFFER_CAPACITY,
  });

  let prevVerbose: boolean | undefined;
  // A verbose flip's `redial()` is a DELIBERATE, in-place reconnect — the
  // rows already shown and the resume cursor both survive, only the query
  // string changes (see the docblock above). To every OTHER consumer of
  // `status()` that must read as "nothing happened": in particular
  // `useTerminalSystemLines` below, which turns any `connecting` transition
  // into a synthetic "reconnecting…" row on the ONE shared buffer every
  // pane renders from (#4397). Without this, flipping verbose in one pane
  // would paint a fabricated reconnect narration into every OTHER pane's
  // history too, contradicting this file's own "the ONLY close that
  // reconnects BY ITSELF is the server's routine ~10-minute recycle"
  // invariant (found in review). `redialing` masks exactly the `connecting`
  // phase of a self-triggered redial as `live`; a genuine drop or refusal
  // during that window still surfaces as `error` untouched, and the flag
  // clears the moment the underlying stream reports `live` again.
  const [redialing, setRedialing] = createSignal(false);
  createEffect(() => {
    const v = verbose();
    if (prevVerbose !== undefined && v !== prevVerbose) {
      setRedialing(true);
      stream.redial();
    }
    prevVerbose = v;
  });
  createEffect(() => {
    if (stream.status() === 'live') setRedialing(false);
  });
  const status = (): StreamStatus =>
    redialing() && stream.status() === 'connecting' ? 'live' : stream.status();

  return { ...stream, status };
}

// ─── synthetic system rows ───────────────────────────────────────────────────

function systemLine(input: {
  readonly id: string;
  readonly ts: string;
  readonly level: TerminalLevel;
  readonly key: `terminal.${string}`;
  readonly params: Record<string, string | number>;
  readonly org: string;
}): TerminalEvent {
  const actor: TerminalActor = 'system';
  return {
    id: input.id,
    ts: input.ts,
    level: input.level,
    actor,
    subject: { kind: 'account', id: input.org, label: input.org },
    line: { key: input.key, params: input.params },
    refs: {},
    correlation_id: null,
    has_raw: false,
  };
}

function bootAuthenticatedLine(ts: string, login: string, org: string): TerminalEvent {
  return systemLine({
    id: 'sys:boot:authenticated',
    ts,
    level: 'info',
    key: 'terminal.boot_authenticated',
    params: { login },
    org,
  });
}

function bootStatusLine(ts: string, online: number, total: number, org: string): TerminalEvent {
  return systemLine({
    id: 'sys:boot:status',
    ts,
    level: 'info',
    key: 'terminal.boot_status',
    params: { online, total },
    org,
  });
}

function bootTailingLine(ts: string, org: string): TerminalEvent {
  return systemLine({
    id: 'sys:boot:tailing',
    ts,
    level: 'ok',
    key: 'terminal.boot_tailing',
    params: { org },
    org,
  });
}

function connectionRecyclingLine(ts: string, generation: number, org: string): TerminalEvent {
  return systemLine({
    id: `sys:connection:recycling:${generation}`,
    ts,
    level: 'info',
    key: 'terminal.stream_recycling',
    params: {},
    org,
  });
}

/**
 * No "resumed N lines" count, by design (plan 101 slice 19 row 19-8). This line
 * is written when status turns `live` again, which `onOpen` sets BEFORE the new
 * connection's first body byte, so any count taken here is 0. Nor can a later
 * moment fix it: the wire does not mark where the `Last-Event-ID` replay ends
 * and fresh rows begin, and a quiet account sends no frame at all.
 */
function connectionResumedLine(ts: string, generation: number, org: string): TerminalEvent {
  return systemLine({
    id: `sys:connection:resumed:${generation}`,
    ts,
    level: 'ok',
    key: 'terminal.stream_resumed',
    params: {},
    org,
  });
}

function connectionDisconnectedLine(ts: string, generation: number, org: string): TerminalEvent {
  return systemLine({
    id: `sys:connection:disconnected:${generation}`,
    ts,
    level: 'warn',
    key: 'terminal.stream_disconnected',
    params: {},
    org,
  });
}

/** What {@link useTerminalSystemLines} reads to decide when a milestone or a
 *  connection transition really happened — every field a REAL signal, never a
 *  timer. */
export type TerminalSystemLineInputs = {
  readonly login: () => string | null;
  readonly org: () => string;
  /**
   * The tenant boundary — `activeAccountId()`, the SAME signal `Terminal.tsx`
   * clears its buffer on. Deliberately not derived from `org` here: they are
   * two seams over one boundary (`org` is `urlOrg()`), and resetting on the
   * wrong one is a real cross-tenant bleed risk, not a style choice — see the
   * module header.
   */
  readonly account: () => string | null;
  readonly workers: () => { readonly online: number; readonly total: number } | null;
  readonly streamStatus: () => StreamStatus;
  /** Injectable for tests; defaults to the real clock. */
  readonly now?: () => string;
};

const defaultNow = (): string => new Date().toISOString();

/** A boot milestone's frozen snapshot — the whole point is that NEITHER field
 *  updates after latch, only together, once. */
type Authenticated = { readonly at: string; readonly login: string };
type FleetStatus = { readonly at: string; readonly online: number; readonly total: number };

/**
 * The boot sequence (three milestones, each latched once) plus the
 * connection-state history (one pair of rows per recycle/drop episode,
 * numbered so each episode keeps its own place in the scrollback rather than
 * overwriting the last). Both reset on an ACCOUNT switch (`input.account()`)
 * — a new org's Terminal greets you again rather than carrying the previous
 * one's "tailing" line.
 */
export function useTerminalSystemLines(
  input: TerminalSystemLineInputs,
): () => readonly TerminalEvent[] {
  const now = input.now ?? defaultNow;

  // Each holds its FULL rendered snapshot, not just a timestamp. `workers()`
  // polls every 5s (`useTerminalStatus`) and `login()` rides `useMe`'s cache —
  // reading either one live in the final memo below, after the milestone had
  // already latched, would let "fleet: 3 of 4 boxes online" silently become
  // "fleet: 2 of 4" under an UNCHANGED timestamp: the exact theatre step 5
  // forbids, a line that keeps rewriting what it once reported. Freezing the
  // whole value (not just `at`) at the moment it latches is what stops that,
  // and it has a second effect: the boot memo below no longer reads `workers()`
  // or `login()` at all, so a routine 5s poll can no longer re-trigger it (or
  // the buffer re-merge / auto-scroll effects downstream of it) once latched.
  const [authenticated, setAuthenticated] = createSignal<Authenticated | null>(null);
  const [fleetStatus, setFleetStatus] = createSignal<FleetStatus | null>(null);
  const [tailingAt, setTailingAt] = createSignal<string | null>(null);
  const [connectionRows, setConnectionRows] = createSignal<readonly TerminalEvent[]>([]);

  let generation = 0;
  let wasLive = false;
  let recycling = false;
  let previousAccount: string | null = null;

  // Reset the whole boot + connection history on an ACCOUNT switch — the SAME
  // signal `Terminal.tsx` clears its buffer on (`activeAccountId()`), not
  // `org()` (`urlOrg()`). Those two can disagree for one reactive tick (only
  // `OrgScope` writes both, and nothing enforces which write lands first), and
  // resetting on the wrong one would let this hook re-merge org A's own
  // "fleet: N boxes online" line into org B's freshly-cleared buffer — an
  // aggregate about another tenant's fleet, rendered under the new org's name.
  createEffect(() => {
    const account = input.account();
    if (previousAccount !== null && account !== previousAccount) {
      setAuthenticated(null);
      setFleetStatus(null);
      setTailingAt(null);
      setConnectionRows([]);
      generation = 0;
      wasLive = false;
      recycling = false;
    }
    previousAccount = account;
  });

  createEffect(() => {
    if (authenticated() !== null) return;
    const login = input.login();
    if (login !== null) setAuthenticated({ at: now(), login });
  });
  createEffect(() => {
    if (fleetStatus() !== null) return;
    const workers = input.workers();
    if (workers !== null) {
      setFleetStatus({ at: now(), online: workers.online, total: workers.total });
    }
  });
  createEffect(() => {
    if (tailingAt() !== null) return;
    if (input.streamStatus() === 'live') setTailingAt(now());
  });

  // `on(...)` on purpose: the branches below read `org()` for a LABEL, never to
  // react to its future changes. `on` restricts re-runs to an actual
  // `streamStatus()` transition, so an org label changing mid-episode cannot
  // re-enter a branch and bump `generation` a second time.
  createEffect(
    on(input.streamStatus, (status) => {
      const org = input.org();
      if (status === 'live') {
        if (recycling) {
          setConnectionRows((prev) => [...prev, connectionResumedLine(now(), generation, org)]);
          recycling = false;
        }
        wasLive = true;
      } else if (status === 'connecting' && wasLive) {
        generation += 1;
        recycling = true;
        setConnectionRows((prev) => [...prev, connectionRecyclingLine(now(), generation, org)]);
      } else if (status === 'error' && wasLive) {
        wasLive = false;
        generation += 1;
        setConnectionRows((prev) => [...prev, connectionDisconnectedLine(now(), generation, org)]);
      }
    }),
  );

  return createMemo((): readonly TerminalEvent[] => {
    const boot: TerminalEvent[] = [];
    const org = input.org();
    const auth = authenticated();
    if (auth !== null) boot.push(bootAuthenticatedLine(auth.at, auth.login, org));
    const status = fleetStatus();
    if (status !== null) boot.push(bootStatusLine(status.at, status.online, status.total, org));
    const tAt = tailingAt();
    if (tAt !== null) boot.push(bootTailingLine(tAt, org));
    return [...boot, ...connectionRows()];
  });
}
